import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import PrivacyBadge from "../components/PrivacyBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const CASE_TEMPLATES = {
    renter_repair: {
        title: "Private rented repair, damp or mould record",
        description: "Record what happened, the dates you contacted the landlord or agent, and the factual response received. Keep any photos, messages or reports as private evidence unless you choose to share them.",
        category: "maintenance",
        visibility: "private",
    },
};

const STATUS_STYLES = {

    open: "bg-blue-50 text-blue-700 border-blue-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-slate-100 text-slate-600 border-slate-200",
};

function StatusBadge({ status }) {
    return (
        <span
            data-testid={`case-status-${status}`}
            className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${STATUS_STYLES[status] || STATUS_STYLES.open}`}
        >
            {status?.replace("_", " ")}
        </span>
    );
}

export default function Cases() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();

    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: "",
        description: "",
        category: "maintenance",
        visibility: "private",
    });
    const [submitting, setSubmitting] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const res = await api.get("/cases");
            setCases(res.data || []);
        } catch (e) {
            toast.error("Failed to load cases");
        } finally {
            setLoading(false);
        }
    };

        useEffect(() => {
        fetchAll();
    }, []);

    useEffect(() => {
        const template = CASE_TEMPLATES[searchParams.get("template")];
        if (template && user?.role !== "agency_viewer") {
            setForm(template);
            setOpen(true);
        }
    }, [searchParams, user?.role]);

    const createCase = async (e) => {

        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/cases", form);
            toast.success("Case created");
            setOpen(false);
            setForm({ title: "", description: "", category: "maintenance", visibility: "private" });
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="cases-page">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Cases</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">All cases</h1>
                    <p className="text-sm text-slate-500 mt-1">Track issues, share with household or estate, or keep private.</p>
                </div>
                {user?.role !== "agency_viewer" && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="create-case-button">
                                <Plus size={16} className="mr-2" /> New case
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="create-case-dialog">
                            <DialogHeader>
                                <DialogTitle>Create case</DialogTitle>
                                <DialogDescription>
                                    New cases start private or household-visible. Share wider from the case page after reviewing consent and linked-document visibility.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={createCase} className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="c-title">Title</Label>
                                    <Input id="c-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="case-title-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="c-desc">Description</Label>
                                    <Textarea id="c-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="case-description-input" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label>Category</Label>
                                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                            <SelectTrigger data-testid="case-category-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="parking">Parking</SelectItem>
                                                <SelectItem value="maintenance">Maintenance</SelectItem>
                                                <SelectItem value="safety">Safety</SelectItem>
                                                <SelectItem value="general">General</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Visibility</Label>
                                        <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                                            <SelectTrigger data-testid="case-visibility-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="private">Private (only me)</SelectItem>
                                                <SelectItem value="household">Household</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <div className="text-xs text-slate-500">
                                            Estate or agency sharing is available after creation so consent and document scope can be recorded.
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={submitting} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="submit-case-button">
                                        {submitting ? "Creating…" : "Create"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="cases-table-container">
                <table className="w-full text-sm" data-testid="cases-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">Title</th>
                            <th className="table-header-cell">Category</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Visibility</th>
                            <th className="table-header-cell">Created</th>
                            <th className="table-header-cell"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="table-cell-tight text-slate-500" data-testid="cases-loading">Loading…</td></tr>
                        )}
                        {!loading && cases.length === 0 && (
                            <tr><td colSpan={6} className="table-cell-tight text-slate-500" data-testid="cases-empty">No cases yet.</td></tr>
                        )}
                        {cases.map((c) => (
                            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors duration-150" data-testid={`case-row-${c.id}`}>
                                <td className="table-cell-tight font-medium text-slate-900">
                                    {c.title}
                                    {c.is_reported && (
                                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded bg-slate-100 text-slate-700 border-slate-300" data-testid={`case-reported-pill-${c.id}`}>
                                            Reported
                                        </span>
                                    )}
                                </td>
                                <td className="table-cell-tight capitalize text-slate-700">{c.category}</td>
                                <td className="table-cell-tight"><StatusBadge status={c.status} /></td>
                                <td className="table-cell-tight"><PrivacyBadge visibility={c.visibility} /></td>
                                <td className="table-cell-tight text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                                <td className="table-cell-tight">
                                    <Link to={`/cases/${c.id}`} className="text-slate-700 hover:text-slate-900 underline text-xs" data-testid={`view-case-${c.id}`}>
                                        Open
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
